import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPublicDish } from '../lib/api';
import { Button } from '../components/ui/button';
import ModelViewer from '../components/ModelViewer';
import IngredientLabels from '../components/IngredientLabels';
import { ArrowLeft, ChefHat, Share2, Globe, ShoppingCart, Plus, ImageIcon, Play, Box } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

function resolveDisplayMode(dish) {
    const dt = dish.display_type || 'auto';
    const imgOk = dish.enable_image_view !== false && dish.image_url;
    const vidOk = dish.enable_video_view !== false && dish.video_url;
    const arOk = dish.enable_ar_view !== false && dish.model_url;

    if (dt === 'video' && vidOk) return 'video';
    if (dt === 'ar' && arOk) return 'ar';
    if (dt === 'image' && imgOk) return 'image';
    if (dt === 'auto') {
        if (vidOk) return 'video';
        if (arOk) return 'ar';
        if (imgOk) return 'image';
    }
    // Fallback
    if (vidOk) return 'video';
    if (arOk) return 'ar';
    if (imgOk) return 'image';
    return 'none';
}

function getAvailableModes(dish) {
    const modes = [];
    if (dish.enable_image_view !== false && dish.image_url) modes.push('image');
    if (dish.enable_video_view !== false && dish.video_url) modes.push('video');
    if (dish.enable_ar_view !== false && dish.model_url) modes.push('ar');
    return modes;
}

export default function DishDetail() {
    const { slug, dishId } = useParams();
    const navigate = useNavigate();
    const { t, lang, setLang, rtl } = useLanguage();
    const { addItem, totalItems } = useCart();
    const videoRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [cartAdded, setCartAdded] = useState(false);
    const [activeMode, setActiveMode] = useState(null);

    useEffect(() => {
        const fetchDish = async () => {
            try {
                const response = await getPublicDish(slug, dishId);
                setData(response.data);
            } catch (err) {
                setError(err.response?.status === 404 ? 'Dish not found' : 'Failed to load dish');
            } finally {
                setLoading(false);
            }
        };
        fetchDish();
    }, [slug, dishId]);

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: data?.dish?.name,
                    text: `Check out ${data?.dish?.name} at ${data?.restaurant?.name}`,
                    url: url
                });
            } catch { /* cancelled */ }
        } else {
            navigator.clipboard.writeText(url);
            toast.success('Link copied!');
        }
    };

    const handleAddToCart = () => {
        addItem(data.dish, slug);
        setCartAdded(true);
        toast.success(`${data.dish.name} added to cart!`);
        setTimeout(() => setCartAdded(false), 2000);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
                <motion.div
                    className="flex flex-col items-center gap-5"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="relative w-14 h-14">
                        <div className="absolute inset-0 border-2 border-[#C9A227]/20 rounded-full" />
                        <div className="absolute inset-0 border-2 border-[#C9A227] border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-[#C9A227]/70 text-xs tracking-[0.2em] uppercase">{t('loading')}</p>
                </motion.div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D]">
                <motion.div
                    className="text-center px-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <ChefHat className="w-20 h-20 text-[#C9A227]/20 mx-auto mb-6" />
                    <h1 className="text-xl font-light text-white/80 mb-3 tracking-tight">{error || t('not_found')}</h1>
                    <Button 
                        onClick={() => navigate(`/menu/${slug}`)} 
                        className="mt-4 bg-[#C9A227] text-white rounded-full px-8"
                    >
                        {t('back_to_menu')}
                    </Button>
                </motion.div>
            </div>
        );
    }

    const { restaurant, dish } = data;
    const labels = data.labels || [];
    const primaryColor = restaurant.primary_color || '#C9A227';
    const bgColor = restaurant.background_color || '#0D0D0D';
    const isDark = ['#0d0d0d', '#1a1a1a', '#000000', '#000'].includes(bgColor.toLowerCase());
    const displayMode = resolveDisplayMode(dish);
    const availableModes = getAvailableModes(dish);
    const currentMode = activeMode && availableModes.includes(activeMode) ? activeMode : displayMode;
    const showTabs = availableModes.length > 1;

    const modeLabels = { image: 'Image', video: 'Video', ar: 'AR' };
    const modeIcons = { image: ImageIcon, video: Play, ar: Box };

    return (
        <div className="min-h-screen" style={{ backgroundColor: bgColor }} dir={rtl ? 'rtl' : 'ltr'}>
            {/* Floating top bar */}
            <motion.div
                className="fixed top-0 left-0 right-0 z-50 px-4 pt-4 pb-2"
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5, ease: 'easeOut' }}
            >
                <div className="flex items-center justify-between max-w-lg mx-auto">
                    <button
                        onClick={() => navigate(`/menu/${slug}`)}
                        className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-2xl transition-all active:scale-90"
                        style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)' }}
                        data-testid="back-to-menu-btn"
                    >
                        <ArrowLeft className="w-[18px] h-[18px] text-white/80" />
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
                            className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-2xl transition-all active:scale-90"
                            style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)' }}
                            data-testid="lang-toggle-dish"
                        >
                            <Globe className="w-[16px] h-[16px] text-white/70" />
                        </button>
                        <button
                            onClick={handleShare}
                            className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-2xl transition-all active:scale-90"
                            style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.06)' }}
                            data-testid="share-btn"
                        >
                            <Share2 className="w-[18px] h-[18px] text-white/80" />
                        </button>
                    </div>
                </div>
            </motion.div>

            {/* Floating Cart Button */}
            {totalItems > 0 && (
                <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={() => navigate(`/menu/${slug}/cart`)}
                    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl active:scale-90 transition-all"
                    style={{ backgroundColor: primaryColor, boxShadow: `0 8px 30px ${primaryColor}40` }}
                    data-testid="floating-cart-btn"
                >
                    <ShoppingCart className="w-5 h-5 text-white" />
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white text-[10px] font-bold flex items-center justify-center" style={{ color: primaryColor }}>
                        {totalItems}
                    </span>
                </motion.button>
            )}

            {/* Media Hero */}
            <div className="relative h-[58vh] sm:h-[62vh]" data-testid="dish-media-container">
                {/* Mode Tabs */}
                {showTabs && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-1 p-1 rounded-full backdrop-blur-2xl"
                        style={{ backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                        {availableModes.map(mode => {
                            const Icon = modeIcons[mode];
                            const isActive = currentMode === mode;
                            return (
                                <button
                                    key={mode}
                                    onClick={() => { setActiveMode(mode); setVideoLoaded(false); }}
                                    className="px-4 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 transition-all"
                                    style={isActive ? { backgroundColor: primaryColor, color: '#fff' } : { color: 'rgba(255,255,255,0.6)' }}
                                    data-testid={`mode-tab-${mode}`}
                                >
                                    <Icon className="w-3 h-3" /> {modeLabels[mode]}
                                </button>
                            );
                        })}
                    </motion.div>
                )}

                {/* === Video Mode === */}
                {currentMode === 'video' && (
                    <>
                        <div className="w-full h-full overflow-hidden relative">
                            {!videoLoaded && dish.image_url && (
                                <motion.img
                                    src={dish.image_url}
                                    alt={dish.name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    initial={{ opacity: 0.6, scale: 1.08 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 1.2 }}
                                />
                            )}
                            <motion.video
                                ref={videoRef}
                                src={dish.video_url}
                                className="w-full h-full object-cover"
                                autoPlay muted loop playsInline
                                onCanPlayThrough={() => setVideoLoaded(true)}
                                initial={{ scale: 1.12, opacity: 0 }}
                                animate={{ scale: 1, opacity: videoLoaded ? 1 : 0 }}
                                transition={{ duration: 8, ease: 'easeOut' }}
                                data-testid="dish-video"
                            />
                        </div>
                        {/* Cinematic overlays */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: 'linear-gradient(180deg, rgba(0,0,0,0.25) 0%, transparent 25%, transparent 55%, rgba(0,0,0,0.55) 100%)'
                        }} />
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.3) 100%)'
                        }} />
                        {labels.length > 0 && <IngredientLabels labels={labels} primaryColor={primaryColor} />}
                    </>
                )}

                {/* === AR Mode === */}
                {currentMode === 'ar' && (
                    <>
                        <div className="w-full h-full overflow-hidden">
                            <ModelViewer
                                src={dish.model_url}
                                alt={dish.name}
                                primaryColor={primaryColor}
                                backgroundColor={isDark ? '#0D0D0D' : '#f5f5f5'}
                                className="w-full h-full"
                            />
                        </div>
                        {labels.length > 0 && <IngredientLabels labels={labels} primaryColor={primaryColor} />}
                    </>
                )}

                {/* === Image Mode with slow zoom === */}
                {currentMode === 'image' && (
                    <>
                        <div className="w-full h-full overflow-hidden">
                            <motion.img
                                src={dish.image_url}
                                alt={dish.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                initial={{ scale: 1.15, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                            />
                        </div>
                        {/* Cinematic overlays */}
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: `linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 30%, transparent 55%, ${bgColor} 100%)`
                        }} />
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.25) 100%)'
                        }} />
                        {labels.length > 0 && <IngredientLabels labels={labels} primaryColor={primaryColor} />}
                    </>
                )}

                {/* No media */}
                {currentMode === 'none' && (
                    <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: isDark ? '#111' : '#f5f5f5' }}
                    >
                        <ChefHat className="w-24 h-24" style={{ color: `${primaryColor}20` }} />
                    </div>
                )}
            </div>

            {/* ===== Content Sheet ===== */}
            <motion.div
                className="relative -mt-8 rounded-t-[36px] px-6 pt-10 pb-12 min-h-[44vh]"
                style={{
                    backgroundColor: isDark ? '#111' : '#fff',
                    boxShadow: '0 -20px 60px rgba(0,0,0,0.15)',
                }}
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
                <div className="max-w-lg mx-auto">
                    {/* Restaurant tag */}
                    <motion.p
                        className="text-[10px] font-medium uppercase tracking-[0.2em] mb-3"
                        style={{ color: `${primaryColor}99` }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        {restaurant.name}
                    </motion.p>

                    {/* Name + Price */}
                    <motion.div
                        className="flex items-start justify-between gap-4 mb-5"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 0.5 }}
                    >
                        <h1
                            className="text-[26px] sm:text-3xl font-light tracking-tight leading-tight"
                            style={{ color: isDark ? '#fff' : '#111' }}
                        >
                            {dish.name}
                        </h1>
                        <p
                            className="text-2xl font-semibold whitespace-nowrap pt-0.5"
                            style={{ color: primaryColor }}
                        >
                            QAR {dish.price.toFixed(0)}
                        </p>
                    </motion.div>

                    {/* Thin gold separator */}
                    <motion.div
                        className="h-px mb-5"
                        style={{ background: `linear-gradient(90deg, ${primaryColor}40, transparent)` }}
                        initial={{ scaleX: 0, originX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.55, duration: 0.6 }}
                    />

                    {/* Description */}
                    {dish.description && (
                        <motion.p
                            className="text-[15px] leading-relaxed mb-8"
                            style={{ color: isDark ? 'rgba(255,255,255,0.5)' : '#777' }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55 }}
                        >
                            {dish.description}
                        </motion.p>
                    )}

                    {/* Add to Cart */}
                    <motion.div
                        className="mb-10"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65 }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <Button
                            onClick={handleAddToCart}
                            className="w-full h-[56px] rounded-2xl text-[15px] font-semibold gap-3 transition-all duration-500 relative overflow-hidden"
                            style={{
                                backgroundColor: cartAdded ? '#22c55e' : primaryColor,
                                color: '#fff',
                                boxShadow: cartAdded
                                    ? '0 8px 30px rgba(34,197,94,0.3)'
                                    : `0 8px 30px ${primaryColor}30`,
                            }}
                            data-testid="add-to-cart-btn"
                        >
                            <AnimatePresence mode="wait">
                                {cartAdded ? (
                                    <motion.span key="added" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="flex items-center gap-2">
                                        <ShoppingCart className="w-5 h-5" /> Added!
                                    </motion.span>
                                ) : (
                                    <motion.span key="add" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} className="flex items-center gap-2">
                                        <Plus className="w-5 h-5" /> Add to Cart &middot; QAR {dish.price.toFixed(0)}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </Button>
                    </motion.div>

                    {/* AR indicator */}
                    {dish.model_url && currentMode !== 'ar' && dish.enable_ar_view !== false && (
                        <motion.div
                            className="rounded-2xl p-4 mb-8"
                            style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#f8f8f8', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#eee'}` }}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.75 }}
                        >
                            <div className="flex items-center gap-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${primaryColor}15` }}
                                >
                                    <span style={{ color: primaryColor }}>🥽</span>
                                </div>
                                <div>
                                    <p className="font-medium text-sm" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
                                        {t('ar_available')}
                                    </p>
                                    <p className="text-xs" style={{ color: isDark ? '#555' : '#999' }}>
                                        {t('ar_desc')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* About section */}
                    <motion.div
                        className="border-t pt-7"
                        style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : '#eee' }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.85 }}
                    >
                        <p
                            className="text-[10px] uppercase tracking-[0.2em] mb-3"
                            style={{ color: isDark ? 'rgba(255,255,255,0.25)' : '#bbb' }}
                        >
                            {t('about_dish')}
                        </p>
                        <p className="text-sm leading-relaxed" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#888' }}>
                            {dish.description || `Enjoy the exquisite taste of ${dish.name}, carefully prepared by our expert chefs.`}
                        </p>
                    </motion.div>
                </div>
            </motion.div>
        </div>
    );
}
